import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Facebook, Instagram, Phone, MapPin, Clock, Mail } from "lucide-react";
import { SITE } from "@/constants/testIds";
import api from "@/lib/api";

export const Footer = () => {
    const [info, setInfo] = useState(null);
    useEffect(() => {
        api.siteInfo().then(setInfo).catch(() => {});
    }, []);

    return (
        <footer
            data-testid={SITE.siteFooter}
            className="bg-sand/70 border-t border-border/80"
        >
            <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-14">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
                    {/* Brand */}
                    <div>
                        {info?.logo_url ? (
                            <img
                                data-testid={SITE.footerLogo}
                                src={info.logo_url}
                                alt="Choice Ayurveda"
                                className="h-14 w-auto"
                                loading="lazy"
                            />
                        ) : (
                            <div className="font-display text-2xl text-moss">Choice Ayurveda</div>
                        )}
                        <p className="mt-4 max-w-xs text-sm text-foreground/70">
                            {info?.description || "Access the wisdom of Ayurveda on Maui or online."}
                        </p>
                        <div
                            data-testid={SITE.footerSocialLinks}
                            className="mt-5 flex items-center gap-3"
                        >
                            {info?.socials?.map((s) => (
                                <a
                                    key={s.name}
                                    href={s.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    aria-label={s.name}
                                    className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-cream border border-border/80 text-moss hover:text-terracotta hover:border-terracotta/40 transition-colors"
                                >
                                    {s.icon === "facebook" ? (
                                        <Facebook className="h-4 w-4" strokeWidth={1.75} />
                                    ) : (
                                        <Instagram className="h-4 w-4" strokeWidth={1.75} />
                                    )}
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Quick links */}
                    <div data-testid={SITE.footerQuickLinks}>
                        <p className="font-accent text-xs text-foreground/60">EXPLORE</p>
                        <ul className="mt-4 space-y-2 text-sm">
                            {[
                                ["Home", "/"],
                                ["About Riley", "/about"],
                                ["Treatments", "/treatments"],
                                ["Packages", "/packages"],
                                ["Why Ayurveda", "/why-ayurveda"],
                                ["Blog", "/blog"],
                                ["FAQ", "/faq"],
                                ["Testimonials", "/testimonials"],
                                ["Shop", "/shop"],
                                ["Contact", "/contact"],
                                ["New Client Intake", "/intake"],
                            ].map(([label, to]) => (
                                <li key={to}>
                                    <Link
                                        to={to}
                                        className="text-foreground/80 hover:text-terracotta transition-colors"
                                    >
                                        {label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Contact */}
                    <div>
                        <p className="font-accent text-xs text-foreground/60">VISIT US</p>
                        <ul className="mt-4 space-y-3 text-sm">
                            <li className="flex items-start gap-2">
                                <MapPin className="mt-0.5 h-4 w-4 text-moss" strokeWidth={1.75} />
                                <span className="text-foreground/80">{info?.address}</span>
                            </li>
                            <li className="flex items-center gap-2">
                                <Phone className="h-4 w-4 text-moss" strokeWidth={1.75} />
                                <a
                                    href={info?.phone_link || "#"}
                                    className="text-foreground/80 hover:text-terracotta"
                                >
                                    {info?.phone}
                                </a>
                            </li>
                            <li className="flex items-center gap-2">
                                <Mail className="h-4 w-4 text-moss" strokeWidth={1.75} />
                                <a
                                    href={`mailto:${info?.email || ""}`}
                                    className="text-foreground/80 hover:text-terracotta"
                                >
                                    {info?.email}
                                </a>
                            </li>
                        </ul>
                    </div>

                    {/* Hours */}
                    <div>
                        <p className="font-accent text-xs text-foreground/60">HOURS</p>
                        <ul className="mt-4 space-y-2 text-sm">
                            {info?.hours?.map((h) => (
                                <li key={h.label} className="flex items-start gap-2">
                                    <Clock className="mt-0.5 h-4 w-4 text-moss" strokeWidth={1.75} />
                                    <div>
                                        <p className="font-medium text-foreground/85">{h.label}</p>
                                        <p className="text-foreground/70">{h.value}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                <div className="mt-12 flex flex-col sm:flex-row items-center justify-between gap-3 border-t border-border/80 pt-6 text-xs text-foreground/60">
                    <p>
                        © {new Date().getFullYear()} Choice Ayurveda. All rights reserved.
                    </p>
                    <div className="flex items-center gap-4">
                        <a href="#" className="hover:text-terracotta">
                            Privacy Policy
                        </a>
                        <a href="#" className="hover:text-terracotta">
                            Terms
                        </a>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
